import React, { Fragment, useEffect, useRef } from 'react';
import { fetchPostQuery, useQuery } from '/src/api';
import { useModalsContext, useStoreConfigContext } from '/src/providers';
import {
    Errors,
    Form,
    Loader,
    useForm,
} from '/src/components';
import { CREATE_ACCOUNT } from '/src/consts';
import graphQLQueries from './graph-ql-queries';
import * as styles from './styles.scss';

const getFirstName = () => {
    const { firstname = '' } = JSON.parse(sessionStorage.getItem(CREATE_ACCOUNT)) || {};

    return firstname;
};

const getLastName = () => {
    const { lastname = '' } = JSON.parse(sessionStorage.getItem(CREATE_ACCOUNT)) || {};

    return lastname;
};

const FormCheckoutAddress = ({
    address,
    setAddressOnCart,
    isAuth,
    dataCountries = [],
    isLoading,
}) => {
    const { translate } = useStoreConfigContext();
    const { hideModal } = useModalsContext();
    const handle = useRef(null);
    const refAutocompleteResults = useRef(null);

    const {
        data: autocomplete,
        isLoading: isLoadingAutocomplete,
        runQuery: runQueryAutocomplete,
        setValues: setValuesAutocomplete
    } = useQuery({
        query: graphQLQueries.autocomplete,
        queryKey: ['autocomplete', 'items'],
        isMethodPost: true,
    });

    const {
        data: autocompleteFind,
        isLoading: isLoadingAutocompleteFind,
        runQuery: runQueryAutocompleteFind,
        setValues: setValuesAutocompleteFind
    } = useQuery({
        query: graphQLQueries.autocompleteFind,
        queryKey: ['autocompleteFind', 'items'],
        isMethodPost: true,
    });

    const {
        data,
        errors,
        setErrors,
        fieldsErrors,
        handleChange,
        validateData,
        setFormValues,
    } = useForm(
        {
            firstname: getFirstName(),
            lastname: getLastName(),
            street1: '',
            street2: '',
            city: '',
            postcode: '',
            country_code: '',
            telephone: '',
            save_in_address_book: false,
        },
        {
            firstname: { isRequired: true },
            lastname: { isRequired: true },
            street1: { isRequired: true },
            city: { isRequired: true },
            postcode: { isRequired: true },
            telephone: { isRequired: true },
            country_code: { isRequired: true },
        }
    );

    const handleSubmit = e => {
        e.preventDefault();
        const isValidData = validateData();

        if (isValidData) {
            const { street1, street2, ...props } = data;
            setAddressOnCart({ ...props, street: [street1, street2] })
                .then(() => hideModal())
                .catch(errors => setErrors({ errors }));
        }
    };

    const handleAutocomplete = e => {
        handleChange(e);
        clearTimeout(handle.timeout);
        handle.timeout = setTimeout(() => {
            setValuesAutocompleteFind({ data: null });
            runQueryAutocomplete({
                variables: {
                    input: {
                        country: data.country_code,
                        search_term: e.target.value,
                    },
                }
            })
        },600);
    };

    const handleAutocompleteFind = id => runQueryAutocompleteFind({
        variables: {
            input: {
                country: data.country_code,
                search_term: id,
            },
        }
    });

    const handleRetrieveAddress = id => {
        setValuesAutocomplete({ isLoading: true });

        fetchPostQuery({
            query: graphQLQueries.retrieveAddress,
            variables: { id },
        }).then(({ retrieveAddress }) => {
            const {
                street,
                city,
                country,
                postcode
            } = retrieveAddress;

            setFormValues({
                data: {
                    ...data,
                    street1: street[0] || '',
                    street2: street[1] || '',
                    city,
                    postcode,
                    country_code: country.code,
                }
            });

            setValuesAutocomplete({ data: null, isLoading: false });
            setValuesAutocompleteFind({ data: null });
        }).catch(() => {
            setValuesAutocomplete({ isLoading: false });
        });
    };

    useEffect(() => {
        if (dataCountries.length && !data.country_code) {
            handleChange({
                target: {
                    value: dataCountries[0].id,
                    name: 'country_code'
                }
            });
        }
    }, [dataCountries]);

    useEffect(() => {
        const {
            firstname,
            lastname,
            street,
            city,
            postcode,
            country,
            telephone,
        } = address || {};

        if (address) {
            setFormValues({
                data: {
                    ...data,
                    firstname,
                    lastname,
                    street1: street[0] || '',
                    street2: street[1] || '',
                    city,
                    postcode,
                    country_code: country.code,
                    telephone,
                }
            })
        }
    }, [address]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (refAutocompleteResults.current && !refAutocompleteResults.current.contains(event.target)) {
                setValuesAutocomplete({ data: null });
                setValuesAutocompleteFind({ data: null });
            }
        }

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [refAutocompleteResults]);

    return (
        <>
            <Errors errors={errors} translate={translate} />
            <Form onSubmit={handleSubmit}>
                <Form.Column mb>
                    <Form.Input
                        label={`${translate('First name')} *`}
                        type="text"
                        name="firstname"
                        value={data.firstname}
                        onChange={handleChange}
                        error={translate(fieldsErrors.firstname)}
                    />
                </Form.Column>
                <Form.Column mb>
                    <Form.Input
                        label={`${translate('Last name')} *`}
                        type="text"
                        name="lastname"
                        value={data.lastname}
                        onChange={handleChange}
                        error={translate(fieldsErrors.lastname)}
                    />
                </Form.Column>
                <Form.Column mb>
                    <Form.Input
                        label={`${translate('Telephone') } *`}
                        type="tel"
                        name="telephone"
                        value={data.telephone}
                        onChange={handleChange}
                        error={translate(fieldsErrors.telephone)}
                    />
                </Form.Column>
                <Form.Column
                    className={styles.autocomplete}
                    mb
                >
                    <div { ...(!!autocomplete?.length ? { ref: refAutocompleteResults } : {}) }>
                        <Form.Input
                            label={`${translate('Postcode')} *`}
                            type="text"
                            name="postcode"
                            value={data.postcode}
                            onChange={e => handleAutocomplete(e)}
                            error={translate(fieldsErrors.postcode)}
                        />
                        {
                            !!autocomplete?.length &&
                            <div className={styles.wrapper}>
                                <Loader isLoading={isLoadingAutocomplete || isLoadingAutocompleteFind}>
                                    <div className={styles.list}>
                                        <button
                                            className={`${!!autocompleteFind?.length ? styles.show : styles.hide}`}
                                            onClick={() => setValuesAutocompleteFind({ data: null })}
                                            type="button"
                                            disabled={!autocompleteFind?.length}
                                        >
                                            <svg width='14' height='8' viewBox='0 0 14 8' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                                <path d='M1 7L7 1L13 7' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                                            </svg>
                                        </button>
                                        <div className={styles.results}>
                                            {
                                                (autocompleteFind || autocomplete).map(({ id, type, suggestion }) => (
                                                    <Fragment key={id}>
                                                        {
                                                            type !== 'Address' &&
                                                            <button
                                                                className={styles.contain}
                                                                onClick={() => handleAutocompleteFind(id)}
                                                                type="button"
                                                            >
                                                                {suggestion}
                                                            </button>
                                                        }
                                                        {
                                                            type === 'Address' &&
                                                            <button
                                                                className={styles.address}
                                                                onClick={() => handleRetrieveAddress(id)}
                                                                type="button"

                                                            >
                                                                {suggestion}
                                                            </button>
                                                        }
                                                    </Fragment>
                                                ))
                                            }
                                        </div>
                                    </div>
                                </Loader>
                            </div>
                        }
                    </div>
                </Form.Column>
                <Form.Column mb>
                    <Form.Input
                        label={`${translate('Street')} *`}
                        type="text"
                        name="street1"
                        value={data.street1}
                        onChange={handleChange}
                        error={translate(fieldsErrors.street1)}
                    />
                </Form.Column>
                <Form.Column mb>
                    <Form.Input
                        type="text"
                        name="street2"
                        value={data.street2}
                        onChange={handleChange}
                        error={translate(fieldsErrors.street2)}
                    />
                </Form.Column>
                <Form.Column mb>
                    <Form.Input
                        label={`${translate('City')} *`}
                        type="text"
                        name="city"
                        value={data.city}
                        onChange={handleChange}
                        error={translate(fieldsErrors.city)}
                    />
                </Form.Column>
                <Form.Column mb>
                    <Form.Select
                        label={`${translate('Countries')} *`}
                        name="country_code"
                        onChange={handleChange}
                        value={data.country_code}
                    >
                        {
                            dataCountries.map(({ id, full_name_locale }) => (
                                <Form.Option key={id} value={id}>
                                    {full_name_locale}
                                </Form.Option>
                            ))
                        }
                    </Form.Select>
                </Form.Column>
                {
                    isAuth &&
                    <Form.Column>
                        <Form.Checkbox
                            label={translate('Save in address book')}
                            name="save_in_address_book"
                            checked={data.save_in_address_book}
                            onChange={handleChange}
                        />
                    </Form.Column>
                }
                <Form.Column mt>
                    <Form.Button disabled={isLoading}>
                        {translate('Save')}
                    </Form.Button>
                </Form.Column>
            </Form>
        </>
    );
};

export default FormCheckoutAddress;
